import { createRequest } from '@shared/services';
import { SHOP_THEME_NAME, LAYOUT_SECTION_NAME } from 'shop-ui/constants';
import { type PageLayoutData } from 'shop-ui/types';

export interface GetPageByIdArgsType {
    pageId: string;
}

export interface GetPageMetaArgsType {
    pageIds: string[];
}

export interface PostPageArgsType {
    name: string;
    custom_name: string;
    theme: SHOP_THEME_NAME;
    segment?: string;
}

export interface PutPageArgsType extends GetPageByIdArgsType {
    custom_name: string;
    enabled: boolean;
    theme: SHOP_THEME_NAME;
}

export interface DeletePageArgsType extends GetPageByIdArgsType {}

export interface PageReturnLayout {
    id: string;
    section: LAYOUT_SECTION_NAME;
    layout: PageLayoutData;
}

export interface GetPageMetaReturn {
    title: string;
    segment: string;
    description: string;
    keyword: string[];
    id: string;
}

export interface PutPageMetaArgsType extends Omit<GetPageMetaReturn, 'id'> {
    title: string;
    segment: string;
    description: string;
    keyword: string[];
}

export interface PageReturn {
    // id 是 page id，已建立的頁面 identifier
    id: string;

    // name 是頁面的系統名稱，用於識別頁面。例如：home, product, about, cart
    name: string;

    // custom_name 是使用者命名的頁面名稱，用於顯示在編輯器上。例如：首頁, 產品頁, 關於我們, 購物車
    custom_name: string;

    // enabled 頁面是否啟用的狀態
    enabled: boolean;

    // 頁面目前用到的 layout 資訊。通常會有三組，header, body, footer
    layouts: Array<PageReturnLayout>;

    published_layouts: Array<PageReturnLayout>;

    theme: SHOP_THEME_NAME;
}

export type DeletePageResponse = BasicAPIResponse<{ success: boolean }>;

export type PageResponse = BasicAPIResponse<PageReturn>;

export type PageListResponse = BasicAPIResponse<PageReturn[]>;

export type PageMetaResponse = BasicAPIResponse<Record<string, GetPageMetaReturn>>;

export async function getPage({
    useServerRequest = false,
}: { useServerRequest?: boolean } = {}) {
    const request = createRequest<PageListResponse>({
        url: '/page',
        method: 'GET',
        withAuthorization: true,
        useToastHandler: false,
        useServerRequest,
    });

    return request.execute();
}

export async function getPageById({ pageId: page_id }: GetPageByIdArgsType) {
    const request = createRequest<PageResponse>({
        url: `/page/${page_id}`,
        method: 'GET',
        withAuthorization: true,
        useToastHandler: false,
    });

    return request.execute();
}

export async function postPage({
    name,
    custom_name,
    theme,
    segment = '',
}: PostPageArgsType) {
    const request = createRequest<PageResponse>({
        url: '/page',
        method: 'POST',
        withAuthorization: true,
        useToastHandler: false,
        data: {
            name,
            custom_name,
            theme,
            segment,
        },
    });

    return request.execute();
}

export async function putPage({
    pageId: page_id,
    custom_name,
    enabled,
    theme,
}: PutPageArgsType) {
    const request = createRequest<PageResponse>({
        url: `/page/${page_id}`,
        method: 'PUT',
        withAuthorization: true,
        useToastHandler: false,
        data: {
            custom_name,
            enabled,
            ...(theme && { theme }),
        },
    });

    return request.execute();
}

export async function deletePage({ pageId: page_id }: DeletePageArgsType) {
    const request = createRequest<DeletePageResponse>({
        url: `/page/${page_id}`,
        method: 'DELETE',
        withAuthorization: true,
        useToastHandler: false,
    });

    return request.execute();
}

export async function getPageMeta(args: GetPageMetaArgsType) {
    const { pageIds: page_ids } = args;

    const sequence = page_ids.map((page_id) => ['page_id', page_id]);

    const searchParams = new URLSearchParams(sequence);

    const queryString = searchParams.toString();

    const request = createRequest<PageMetaResponse>({
        url: `/page/meta?${queryString}`,
        method: 'GET',
        withAuthorization: true,
        useToastHandler: false,
    });

    return request.execute();
}

export async function putPageMeta(id: string, metaData: PutPageMetaArgsType) {
    const request = createRequest<PageMetaResponse>({
        url: `/page/meta/${id}`,
        method: 'PUT',
        withAuthorization: true,
        useToastHandler: false,
        data: metaData,
    });

    return request.execute();
}
